import styled from '@emotion/styled';
import { Container } from 'components/Layout';
import {
  MainPageContainer,
  UpperInfoContainer,
} from 'components/PageLayout';
import { createReportURL } from 'core/routes';
import { convertToDate, useGetVehiclesOnSiteReport } from 'pages/hooks';
import { FormEvent, useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { AxiosError } from 'axios';
import { columns, VEHICLES_ON_SITE_REPORT, VEHICLES_ON_SITE_REPORT_INFO, VEHICLES_ON_SITE_REPORT_SHEET_TITLE, VEHICLES_ON_SITE_REPORT_TITLE } from './constants';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { VehiclesOnSiteSearch } from '../../../components/ReportForms/VehiclesOnSiteSearch';
import { ReportProps } from 'pages/ReportsDetails/types';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import DevExtremeReportGrid from 'components/ReportGrid_New';
import { noPDFexportFormats } from 'components/ReportGrid_New/constants';

export function VehiclesOnSite({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const priorFullMonthParam = urlSearchParams.get('priorFullMonth');
  const parsedPriorFullMonth = priorFullMonthParam !== 'false';
  const urlParams = {
    startDate: urlSearchParams.get('startDate'),
    endDate: urlSearchParams.get('endDate'),
    priorFullMonth: parsedPriorFullMonth,
  };

  const queryEnabled = !!urlParams.startDate && !!urlParams.endDate;

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.startDate, urlParams.endDate, urlParams.priorFullMonth]);

  const { vehiclesOnSiteReportData, refetchReport, isReportFetching } =
    useGetVehiclesOnSiteReport(
      {
        startDate: urlParams.startDate,
        endDate: urlParams.endDate,
        priorFullMonth: parsedPriorFullMonth,
      },
      queryEnabled,
      handleError
    );

  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParams.startDate) || null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParams.endDate) || null
  );

  const [priorFullMonth, setPriorFullMonth] = useState<boolean>(parsedPriorFullMonth);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDate(new Date())} for period ${formatDate(startDate)} to ${formatDate(endDate)}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={VEHICLES_ON_SITE_REPORT_TITLE}
          infoMessage={VEHICLES_ON_SITE_REPORT_INFO}
        />
        <VehiclesOnSiteSearch
          handleSubmit={handleSubmit}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          priorFullMonth={priorFullMonth}
          setPriorFullMonth={setPriorFullMonth}
          isDisabled={isReportFetching}
        />
      </UpperInfoContainer>
      {!isReportFetching && vehiclesOnSiteReportData && vehiclesOnSiteReportData?.length === 0 && (
        <NoReportDataMessage />
      )}
      <ReportGridContainer direction="column">
        <DevExtremeReportGrid
          columns={columns}
          displayGroupingPanel
          exportFormats={noPDFexportFormats}
          headerTitleForSheet={VEHICLES_ON_SITE_REPORT_SHEET_TITLE}
          isLoading={isReportFetching}
          reportHeaderTagLine={reportHeaderTagLine}
          rows={vehiclesOnSiteReportData}
          title={VEHICLES_ON_SITE_REPORT}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParams.startDate == dateToString(startDate) &&
      urlParams.endDate == dateToString(endDate) &&
      urlParams.priorFullMonth == priorFullMonth
    )
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (!!startDate && !!endDate) {
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
            priorFullMonth: priorFullMonth,
          },
          reportPath,
        ), { replace: true });
    }
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports(VEHICLES_ON_SITE_REPORT_SHEET_TITLE));
    setToast(true);
  }
}

const ReportGridContainer = styled(Container)`
  padding-left: ${({ theme }) => theme.padding.xl};
  margin-bottom: ${({ theme }) => theme.padding.xl};
  @media (max-width: ${({ theme }) => `${theme.breakpoints.values.md}px`}) {
    padding: 0 20px;
  }
  @media (max-width: ${({ theme }) => `${theme.breakpoints.values.sm}px`}) {
    padding: 0 15px;
  }
`;
